import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { useNavigate, useLocation, useParams } from "react-router-dom";
// components
import { ReactComponent as AssessmentEmpty } from "assets/svg/assessment-empty.svg";
import { ReactComponent as OutlineCross } from "assets/svg/outline-cross.svg";
import { ReactComponent as OutlineUserWarning } from "assets/svg/outline-user-warning.svg";
import GAD7Interpretation from "components/Dashboard/DoctorDashboard/notes-and-forms/components/gad-7-interpretation";
import PHQ9Interpretation from "components/Dashboard/DoctorDashboard/notes-and-forms/components/phq-9-interpretation";
import getFormScore from "components/Dashboard/DoctorDashboard/notes-and-forms/scenes/form-fill-in/utils/getFormScore";
import SendAReminder from "components/Dashboard/DoctorDashboard/patient-charts/components/patient-chart/components/send-a-reminder";
import AutoFocusAlert from "components/auto-focus-alert";
import ConfirmModal from "components/confirm-modal";
import ContentHeader from "components/content-header/ContentHeader";
import IconButton from "components/icon-button";
import StickyFooter from "components/sticky-footer";
import AssessmentSideInfo from "../../components/assessment-side-info/AssessmentSideInfo";
import AutoSavedState from "../../components/auto-saved-state/AutoSavedState";
import FillInContent from "../../components/fill-in-content/FillInContent";
import Scoring from "./scoring";
// styles
import getClasses from "utils/getClasses";
import layout from "../../layout.module.scss";
import styles from "./FormFillIn.module.scss";
// utils
import {
  orchid_decode_key,
  orchid_decrypt,
  orchid_encrypt,
} from "utils/OrchidEncryption";
import { newTabReceiveData } from "utils/newTab";
import { isAssessment } from "../../utils/isAssessment";
import isGAD7Form from "./utils/isGAD7Form";
import isPHQ9Form from "./utils/isPHQ9Form";
import isEPDSForm from "./utils/isEPDSForm";
// services
import completeForm from "services/completeForm";
import createNoteOrFormData from "services/createNoteOrFormData";
import putNoteOrFormData from "services/putNoteOrFormData";
import queryMetaData from "services/queryMetaData";
import querySchema from "services/querySchema";
import querySecureData from "services/querySecureData";
// constants
import messages from "messages";
import { BLOCK_TYPE_MODES } from "../../constants/blockTypeModes";
// types
import { NOTES_TYPES } from "../../types";
// hooks
import FormPrintOrDownloadTrigger from "components/Dashboard/DoctorDashboard/patient-charts/components/patient-chart/components/form-print-or-download-trigger/FormPrintOrDownloadTrigger";
import {
  DISABLE_OLD_TEMPLATE_CONTENT,
  ENABLE_NEW_TEMPLATE_CONTENT,
} from "constants/featureFlags";
import { useUser } from "contexts/UserContext";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import { useTimeOnPageContext } from "contexts/time-on-page";
import useDelayDebouncedFn from "hooks/useDelayDebouncedFn";
import useRetryFn from "hooks/useRetryFn";
import useToggleModal from "hooks/useToggleModal";
import TemplateContent, {
  TemplateContentImperativeHandleRef,
} from "notes-and-forms-v2/components/template-content/TemplateContent";
import { AmplitudePageName, track } from "utils/amplitude";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";

const TYPE = NOTES_TYPES.form;
const AMPLITUDE_PAGE_NAME: AmplitudePageName = "form-fill-in-page";

type TMetaData = typeof TYPE extends "note" ? NoteMetaData : FormMetaData;

type Props = {
  preview?: boolean;
};

const FormFillIn = ({ preview: previewProp }: Props) => {
  const templateContentImperativeRef =
    useRef<TemplateContentImperativeHandleRef>(null);

  const { setPage } = useTimeOnPageContext();
  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  const { isPhonePortrait } = usePhonePortraitContext();
  const user = useUser()?.user;
  const navigate = useNavigate();

  const params = useParams<{
    form_data_id?: string;
    form_revision?: string;
    form_template_id?: string;
    form_template_revision?: string;
  }>();
  const [metaData, setMetaData] = useState<Partial<TMetaData>>();

  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [data, setData] = useState<Record<string, any>>({});

  const [error, setError] = useState<any>();

  const [decodeKey, setDecodeKey] = useState<CryptoKey>();

  const { practiceOwnerOrchidUserId } = useTeamContext();

  // receive new tab data
  useEffect(() => {
    async function initData() {
      if (!params?.form_data_id && window.opener) {
        const data = await newTabReceiveData();
        setMetaData({
          form_json_schema: data.form_json_schema,
          form_ui_schema: data.form_ui_schema,
          schema_v2: data.schema_v2,
        });
      }

      if (params?.form_data_id) {
        const metaData = await queryMetaData({
          type: TYPE,
          id: params?.form_data_id,
          ...(practiceOwnerOrchidUserId && {
            onBehalfOf: practiceOwnerOrchidUserId,
          }),
        });
        setMetaData(metaData);
        if (metaData?.cipher_key) {
          const decodeKey = await orchid_decode_key(metaData?.cipher_key);
          setDecodeKey(decodeKey);

          let nextSecureData: Record<string, any> = {};
          try {
            nextSecureData = await querySecureData({
              type: TYPE,
              id: params?.form_data_id,
              ...(practiceOwnerOrchidUserId && {
                onBehalfOf: practiceOwnerOrchidUserId,
              }),
            });
          } catch (error) {
            /**
             * swallow the error
             * @see https://orchidhealth.atlassian.net/browse/OIM-765
             */
          }

          const data = nextSecureData?.data
            ? JSON.parse(
                (await orchid_decrypt(nextSecureData.data, decodeKey)) || "{}",
              )
            : {};

          setData(data);
        }
      } else if (params?.form_template_id) {
        const template = await querySchema({
          type: TYPE,
          id: params?.form_template_id,
          revision: params?.form_template_revision,
        });
        setMetaData((s) => ({ ...s, ...template }));
      }
      setInitialDataLoaded(true);
    }

    initData().catch(setError);
    // eslint-disable-next-line
  }, []);

  const encryptAndCreate = useCallback(async () => {
    const nextMetaData = {
      form_schema_id: metaData?.form_schema_id ?? 0,
      form_schema_revision: metaData?.form_schema_revision ?? 0,
      ...(metaData &&
        "appointment" in metaData && {
          appointment_id: metaData?.appointment?.appointment_id,
        }),
      ...(metaData &&
        "group_session" in metaData && {
          group_session_id: metaData?.group_session?.group_session_id,
        }),

      ...(metaData &&
        "patient" in metaData && {
          patient_cognito_sub: metaData?.patient?.cognito_sub,
        }),
    };
    const newData = await createNoteOrFormData(TYPE, nextMetaData);
    const decodeKey = await orchid_decode_key(newData.cipher_key);
    setDecodeKey(decodeKey);
    const encrypted = await orchid_encrypt(JSON.stringify(data), decodeKey);
    const nextSecureData = await putNoteOrFormData(TYPE, newData.form_data_id, {
      orchid_pro_cognito_sub: newData.orchid_pro.cognito_sub,
      data: encrypted,
    });
    // add form id to url
    navigate(
      `/pro-dashboard/forms-and-forms/form/${nextSecureData.form_data_id}`,
    );

    setSaved(true);
  }, [data, navigate, metaData]);

  const encryptAndSave = useCallback(async () => {
    let nextDecodeKey = decodeKey;
    if (!nextDecodeKey) {
      if (!metaData?.cipher_key) {
        throw new Error("not having cipher key");
      }
      nextDecodeKey = await orchid_decode_key(metaData?.cipher_key);
      setDecodeKey(decodeKey);
    }
    if (
      nextDecodeKey &&
      metaData?.form_data_id &&
      metaData?.orchid_pro?.cognito_sub
    ) {
      const encrypted = await orchid_encrypt(
        JSON.stringify(data),
        nextDecodeKey,
      );
      await putNoteOrFormData(TYPE, metaData?.form_data_id, {
        data: encrypted,
        orchid_pro_cognito_sub: metaData?.orchid_pro?.cognito_sub,
      });
      setSaved(true);
    }
  }, [
    data,
    decodeKey,
    metaData?.cipher_key,
    metaData?.form_data_id,
    metaData?.orchid_pro?.cognito_sub,
  ]);

  // register with retries
  const { retryCount: saveRetryCount, fnWithRetry: encryptAndSaveWithRetry } =
    useRetryFn(encryptAndSave);
  const {
    retryCount: createRetryCount,
    fnWithRetry: encryptAndCreateWithRetry,
  } = useRetryFn(encryptAndCreate);

  const [saved, setSaved] = useState<boolean>();
  const [busy, setBusy] = useState(false);

  const onSave = useCallback(() => {
    setBusy(true);
    setError(undefined);

    const fn = metaData?.form_data_id
      ? encryptAndSaveWithRetry
      : encryptAndCreateWithRetry;

    return (
      fn &&
      fn()
        .then(() => {
          setSaved(true);
        })
        .catch((err) => {
          setSaved(false);
          setError(err);
        })
        .finally(() => setBusy(false))
    );
  }, [
    encryptAndCreateWithRetry,
    encryptAndSaveWithRetry,
    metaData?.form_data_id,
  ]);

  // debounce on change and then save
  const [registerSetData] = useDelayDebouncedFn([setData], onSave);

  const onSubmit = async () => {
    const isFormValid = templateContentImperativeRef.current?.validate();
    if (!isFormValid) {
      return;
    }

    if (metaData?.form_data_id) {
      try {
        await onSave();
        await completeForm(metaData?.form_data_id);
        if (isAssessment(metaData.form_schema_id)) {
          navigate("/member-dashboard/notes-and-forms/assessment-submitted", {
            state: { metaData: metaData, data: data },
          });
        } else {
          navigate("/member-dashboard/notes-and-forms/form-completed", {
            state: { orchidProInfo: metaData?.orchid_pro },
          });
        }
      } catch (err) {
        setError(err);
      }
    }
  };

  const isUserPro = useMemo(
    () => user?.user_type === "orchid_pro",
    [user?.user_type],
  );

  const isGuardianViewingMinor = useMemo(
    () => !!user?.minors?.length && !!user?.current_minor_user_id,
    [user?.minors, user?.current_minor_user_id],
  );

  const preview = useMemo(() => {
    if (previewProp) {
      return true;
    }
    if (isUserPro) {
      return true;
    }
    return (
      !isGuardianViewingMinor && metaData?.patient?.cognito_sub !== user?.sub
    );
  }, [
    isUserPro,
    metaData?.patient?.cognito_sub,
    previewProp,
    user?.sub,
    isGuardianViewingMinor,
  ]);

  const { pathname } = useLocation();

  const isAssessmentTemplate = useMemo(() => {
    if (
      previewProp &&
      pathname.startsWith("/pro-dashboard/notes-and-forms/view-form-template")
    ) {
      return true;
    }
  }, [previewProp, pathname]);

  // TODO: this part is to be removed when form fill in is open in the same window
  // use the <RouteLeavingGuard /> to remind of unsaved state
  const {
    show: showLeaveConfirm,
    open: openLeaveConfirm,
    close: closeLeaveConfirm,
  } = useToggleModal(false);

  // suicidal: question 9 of PHQ-9 answers 'Nearly every day (3)'
  const suicidal = useMemo(() => {
    if (!metaData?.completed_timestamp) return false;

    const scoreData = data && Object.values(data)[0];

    if (scoreData) {
      const scoreArr = Object.values(scoreData);
      return (
        isPHQ9Form(metaData?.form_json_schema?.title) &&
        scoreArr?.length === 9 &&
        scoreArr[8] === "Nearly every day (3)"
      );
    }

    return false;
  }, [data, metaData?.form_json_schema?.title, metaData?.completed_timestamp]);

  // high risk: either suicidal, or GAD-7 score > 15, or PHQ-9 score >= 20 and <= 27
  const highRisk = useMemo(() => {
    if (!metaData?.completed_timestamp) return false;

    const score = getFormScore(data, metaData?.form_json_schema?.title);
    return (
      suicidal ||
      (!!score &&
        isGAD7Form(metaData?.form_json_schema?.title) &&
        score > 15) ||
      (!!score &&
        isPHQ9Form(metaData?.form_json_schema?.title) &&
        score >= 20 &&
        score <= 27)
    );
  }, [
    data,
    metaData?.form_json_schema?.title,
    suicidal,
    metaData?.completed_timestamp,
  ]);

  const [showSendAReminderModal, setShowSendAReminderModal] = useState(false);

  const assessmentTypes = useMemo(() => {
    const title = metaData?.form_json_schema?.title;
    return {
      isEPDS: isEPDSForm(title),
      isPHQ9: isPHQ9Form(title),
      isGAD7: isGAD7Form(title),
    };
  }, [metaData?.form_json_schema?.title]);

  return (
    <>
      {metaData && metaData?.patient?.cognito_sub && (
        <SendAReminder
          show={showSendAReminderModal}
          onHide={() => setShowSendAReminderModal(false)}
          patientUserId={metaData.patient.cognito_sub}
          form={metaData}
          amplitudePageName={AMPLITUDE_PAGE_NAME}
        />
      )}
      {!isPhonePortrait ? (
        <div className={layout["layout__header"]}>
          <Button
            variant="outline-primary"
            size={"lg"}
            className={layout["layout__btn"]}
            onClick={() => {
              track(AMPLITUDE_PAGE_NAME, "exit-button", "click");
              if (saved === false) {
                openLeaveConfirm();
              } else {
                window.close();
              }
            }}
          >
            {messages.exit}
          </Button>
          <ConfirmModal
            show={showLeaveConfirm}
            labelTitle={messages.areYouSure}
            children={messages.draftIsBeingSaved}
            labelCancel={messages.cancel}
            labelConfirm={messages.continue}
            onCancel={closeLeaveConfirm}
            onConfirm={() => window.close()}
          />
          {metaData?.form_data_id !== undefined && saved !== undefined && (
            <AutoSavedState
              tooltipClassName={styles["form-fill-in__tooltip"]}
              className={layout["layout__save-state"]}
              busy={busy}
              saved={saved}
              retryCount={
                !metaData?.form_data_id ? createRetryCount : saveRetryCount
              }
              tooltip={
                <span>
                  {messages.everyChangeYouMake + " " + messages.click + " "}
                  <strong>{messages.submit + " "}</strong>
                  {messages.toLockYourForm}
                </span>
              }
            />
          )}
          <div className={layout["layout__actions"]}>
            {metaData && data && (
              <FormPrintOrDownloadTrigger partialData={metaData} action="print">
                <Button
                  variant="link"
                  size={"lg"}
                  className={getClasses(
                    styles["print-button"],
                    layout["layout__btn"],
                  )}
                  onClick={() =>
                    track(AMPLITUDE_PAGE_NAME, "print-button", "click")
                  }
                >
                  Print
                </Button>
              </FormPrintOrDownloadTrigger>
            )}
            {metaData?.form_data_id && (
              <>
                {!preview && !metaData?.completed_timestamp && (
                  <Button
                    variant="outline-primary"
                    size={"lg"}
                    className={layout["layout__btn"]}
                    onClick={() => {
                      track(
                        AMPLITUDE_PAGE_NAME,
                        "save-as-draft-button",
                        "click",
                      );
                      onSave();
                    }}
                    disabled={saved}
                  >
                    {messages.saveAsDraft}
                  </Button>
                )}
                {!metaData?.completed_timestamp ? (
                  !isUserPro && (
                    <Button
                      className={layout["layout__btn"]}
                      variant="primary"
                      size={"lg"}
                      disabled={
                        metaData?.form_schema_revision === undefined ||
                        metaData?.form_schema_revision === null ||
                        saved === false ||
                        !Object.keys(data).length
                      }
                      onClick={() => {
                        track(AMPLITUDE_PAGE_NAME, "submit-button", "click");
                        onSubmit();
                      }}
                    >
                      {messages.submit}
                    </Button>
                  )
                ) : (
                  <Button
                    variant="primary"
                    disabled
                    size={"lg"}
                    className={layout["layout__btn"]}
                  >
                    {isUserPro
                      ? messages.patientSubmitted
                      : messages.alreadySubmitted}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <ContentHeader hideBackButton={true}>
          <div>{metaData?.form_json_schema?.title || ""}</div>
          <Scoring metaData={metaData} data={data} />
          <IconButton
            onClick={() => {
              track(AMPLITUDE_PAGE_NAME, "exit-button", "click");
              navigate(-1);
            }}
          >
            <OutlineCross className="gray01" />
          </IconButton>
        </ContentHeader>
      )}
      <Container
        fluid
        className={getClasses(
          layout["layout__content"],
          isPhonePortrait && layout["layout__content--phone"],
        )}
      >
        {/* offset is $navbarHeight + 20px additional height */}
        {error && (
          <AutoFocusAlert variant="danger" error={error} offset={-100} />
        )}
        <Row>
          <Col md={3}>
            <AssessmentSideInfo
              metaData={metaData}
              isUserPro={isUserPro}
              isAssessmentTemplate={isAssessmentTemplate}
              isAssessment={isAssessment(metaData?.form_schema_id)}
            />
          </Col>
          <Col md={7} className={styles["form-fill-in__body"]}>
            {!isPhonePortrait && (
              <h3 className={layout["layout__title"]}>
                {metaData?.form_json_schema?.title || ""}
              </h3>
            )}
            {highRisk && (
              <div className={styles["form-fill-in__suicidal"]}>
                <OutlineUserWarning />
                <span className={"ml-2"}>
                  Elevated risk for Suicidal Ideation
                </span>
              </div>
            )}
            <Scoring metaData={metaData} data={data} />
            {!isAssessmentTemplate &&
            isUserPro &&
            isAssessment(metaData?.form_schema_id) &&
            !metaData?.completed_timestamp ? (
              <div className={styles["form-fill-in__patient-not-filled"]}>
                <AssessmentEmpty />
                <div className={styles["form-fill-in__patient-empty"]}>
                  Your patient has not completed the assignment
                </div>
                <Button
                  onClick={() => {
                    track(AMPLITUDE_PAGE_NAME, "send-reminder-button", "click");
                    setShowSendAReminderModal(true);
                  }}
                >
                  Send Reminder
                </Button>
              </div>
            ) : (
              <>
                {ENABLE_NEW_TEMPLATE_CONTENT && (
                  <TemplateContent
                    imperativeHandleRef={templateContentImperativeRef}
                    templateElements={metaData?.schema_v2?.elements ?? []}
                    templateData={
                      isUserPro && !metaData?.completed_timestamp ? {} : data
                    }
                    onChange={(key, value) => {
                      if (preview) {
                        return;
                      }
                      setSaved(false);
                      registerSetData((prev) => ({ ...prev, [key]: value }));
                    }}
                    disabled={!initialDataLoaded}
                    isLocked={!!metaData?.completed_timestamp}
                    showSuicidalWarningInPhq9={suicidal}
                    showCopyToClipboard={
                      !preview && user?.user_type === "orchid_pro"
                    }
                  />
                )}

                {metaData?.form_json_schema &&
                  metaData?.form_ui_schema &&
                  !DISABLE_OLD_TEMPLATE_CONTENT && (
                    <FillInContent
                      jsonSchema={metaData?.form_json_schema}
                      uiSchema={metaData?.form_ui_schema}
                      formData={
                        isUserPro && !metaData?.completed_timestamp ? {} : data
                      }
                      onChange={(v) => {
                        if (preview) {
                          return;
                        }
                        setSaved(false);
                        registerSetData(v);
                      }}
                      disabled={!!metaData?.completed_timestamp}
                      mode={
                        preview ? BLOCK_TYPE_MODES.print : BLOCK_TYPE_MODES.fill
                      }
                      suicidal={suicidal}
                    />
                  )}
              </>
            )}
            {!isAssessmentTemplate &&
              isUserPro &&
              isAssessment(metaData?.form_schema_id) && (
                <>
                  {assessmentTypes.isGAD7 && <GAD7Interpretation />}
                  {assessmentTypes.isPHQ9 && <PHQ9Interpretation />}
                  {assessmentTypes.isEPDS && (
                    <div className={styles["form-fill-in__epds-container"]}>
                      <div className={styles["form-fill-in__source"]}>
                        Source
                      </div>
                      <div className={styles["form-fill-in__source-text"]}>
                        Cox, J.L., Holden, J.M., and Sagovsky, R. 1987.
                        Detection of postnatal depression: Development of the
                        10-item Edinburgh Postnatal Depression Scale. British
                        Journal of Psychiatry 150:782-786.
                      </div>
                    </div>
                  )}
                </>
              )}
          </Col>
        </Row>
      </Container>
      {isPhonePortrait && (
        <StickyFooter className={styles["form-fill-in__footer"]}>
          {metaData?.form_data_id && (
            <>
              <Col md={6} xs={6} className="pl-0 pr-2">
                {!preview && !metaData?.completed_timestamp && (
                  <Button
                    variant="outline-primary"
                    size={"lg"}
                    className={styles["form-fill-in__btn--phone"]}
                    onClick={() => {
                      track(
                        AMPLITUDE_PAGE_NAME,
                        "save-as-draft-button",
                        "click",
                      );
                      onSave();
                    }}
                    disabled={saved}
                  >
                    {messages.saveAsDraft}
                  </Button>
                )}
              </Col>
              <Col md={6} xs={6} className="pl-2 pr-0">
                {!metaData?.completed_timestamp ? (
                  !isUserPro && (
                    <Button
                      className={styles["form-fill-in__btn--phone"]}
                      variant="primary"
                      size={"lg"}
                      disabled={
                        metaData?.form_schema_revision === undefined ||
                        metaData?.form_schema_revision === null ||
                        saved === false ||
                        !Object.keys(data).length
                      }
                      onClick={() => {
                        track(AMPLITUDE_PAGE_NAME, "submit-button", "click");
                        onSubmit();
                      }}
                    >
                      {messages.submit}
                    </Button>
                  )
                ) : (
                  <Button
                    variant="primary"
                    disabled
                    size={"lg"}
                    className={styles["form-fill-in__btn--phone"]}
                  >
                    {isUserPro
                      ? messages.patientSubmitted
                      : messages.alreadySubmitted}
                  </Button>
                )}
              </Col>
            </>
          )}
        </StickyFooter>
      )}
    </>
  );
};

export default FormFillIn;
